import _ from "lodash";
import update from "immutability-helper";
import React from "react";

import ACLStore from "lib/aclStore";
import {
  generateRandomId,
  getMemoizedFunction,
  getMemoizedLangFunction,
  isUserTypeStaff,
} from "Utils";
import {
  UNIT_FLOW_REVAMP_PERMISSION,
  ENABLE_ASSESSMENTS_IN_PYP,
  ENABLE_AI_TUTOR,
  OPEN_AND_CREATE_ASSIGNMENTS_IN_EDIT_MODE,
  ENABLE_GRADE_WISE_ASSESSMENT,
} from "Constants/permissionConstants";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CREATE_AI_TUTOR,
  ASSESSMENT_TYPE_AI_TUTOR,
  ASSESSMENT_TYPE_QUICK_TASK,
} from "Constants/stringConstants";

import {
  ImportOutlined,
  UnitPlanOutlined,
  LeLibraryOutlined,
  JournalOutlined,
  SubsectionListOutlined,
  LeOutlined,
  FaOutlined,
  SaOutlined,
  AssessmentAddOutlined,
  WorksheetClipboardOutlined,
  LinkOutlined,
  NoteOutlined,
  UploadOutlined,
  GoogleDriveColored,
  OneDriveColored,
  DeviceDesktopOutlined,
  SectionListOutlined,
  AiOutlined,
  PencilLineOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SwitchArrowsOutlined,
  CopyOutlined,
  StudentAddOutlined,
  LinkSimpleHorizontalOutlined,
  AddToLibraryOutlined,
  CaretOpenedFilled,
  CaretClosedFilled,
  PrintOutlined,
  ReloadArrowOutlined,
  DotsHorizontalOutlined,
  EyeOutlined,
} from "@toddle-edu/ds-icons";
import { Avatar } from "@toddle-edu/ds-web";

import { splitAssessmentAndResourceTypesMemoized } from "Utils/EntityFlowUtils";
import { getGroupOrAssessmentTypes } from "AppComponents/Gradebook/modules/GradebookGraphqlHelpers";
import { shouldShowImportFromToddleLibrary } from "ToddleLibrary/modules/ToddleLibraryUtils";
import { getUnitFlowItemFromCache } from "./GraphqlHelper";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";
import {
  MAX_DEPTH,
  MAX_FOLDER_DEPTH,
} from "UIComponents/NestedAccordion/constants";
import { getUserInfo, isToddleLibraryRoute } from "Login/modules/LoginModule";
import { getIsProSchool } from "UnitPlans/modules/Utils";
import { getAssessmentTemplatesFromCache } from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";
import {
  checkIsUnitPlanViewRevampEnabledInLibrary,
  getIsAssessmentTypeConfigurationEnabled,
} from "Utils/ACLStoreUtils";
import { getTaskTypeAvatarName } from "Utils/EntityFlowUtils";

export const DEFAULT_FILTERS = {
  selectedItemTypes: "ALL",
  selectedAddedBy: "ALL",
  searchText: "",
  selectedTaskTypeIds: "ALL",
};

export const PUSHER_EVENTS = {
  FEED_UPDATE: "unitFlowFeedUpdate",
  RESOURCE_UPDATE: "unitFlowResourceUpdate",
};

export const isUnitFlowRevampEnabled = () => {
  const isToddleLibrary = isToddleLibraryRoute();
  return isToddleLibrary
    ? checkIsUnitPlanViewRevampEnabledInLibrary()
    : ACLStore.can(UNIT_FLOW_REVAMP_PERMISSION);
};

const ENTITY_FLOW_ITEM_CATEGORIES = [
  "FOLDER",
  "ENTITY_FLOW_RESOURCE",
  "ASSESSMENT",
];

const getUnitFlowFeedVariables = ({
  unitPlanId,
  filters,
  userInfo: _userInfo,
  curriculumType,
  resourcesOnly = false,
  availableCategories = [],
}) => {
  const userInfo = _userInfo || getUserInfo();
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const unitFlowFilters = _.mapValues(filters, (value, key) => {
    const isAllOption = _.isEqual(value, "ALL") && key != "searchText";
    return isAllOption ? [] : value;
  });

  const {
    selectedItemTypes = [],
    selectedAddedBy = [],
    searchText = "",
    selectedTaskTypeIds = [],
  } = unitFlowFilters;

  const createdBy = (
    _.isArray(selectedAddedBy)
      ? _.includes(selectedAddedBy, "ME")
      : selectedAddedBy == "ME"
  )
    ? userInfo?.id
    : [];

  const {
    selectedAssessmentTypes,
    selectedResourceTypes,
  } = splitAssessmentAndResourceTypesMemoized({ selectedItemTypes });

  let assessmentFilters = {};

  // Add taskTypeIds when FF is enabled
  const taskTypeIds = isAssessmentTypeConfigurationEnabled
    ? selectedTaskTypeIds
    : undefined;

  if (isAssessmentTypeConfigurationEnabled) {
    // When FF is on, don't include assessmentType and groupTypes, only taskTypeIds
    assessmentFilters = { taskTypeIds };
  } else {
    // When FF is off, use existing logic with assessmentTypes and groupTypes
    const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes(
      {
        curriculumType,
        selectedAssessmentTypes,
      }
    );

    assessmentFilters = {
      assessmentType: assessmentTypes,
      groupTypes,
    };
  }

  let itemTypes = ["FILE"];
  if (!resourcesOnly) {
    if (!_.isEmpty(availableCategories)) {
      itemTypes = _.filter(ENTITY_FLOW_ITEM_CATEGORIES, category =>
        availableCategories.includes(category)
      );
    } else {
      itemTypes = ENTITY_FLOW_ITEM_CATEGORIES;
    }
  }

  return {
    parentId: unitPlanId,
    parentType: "UNIT_PLAN",
    filters: {
      type: resourcesOnly ? "UNIT_RESOURCE" : "UNIT_FLOW",
      searchText,
      itemTypes: itemTypes,
      attachmentTypes: selectedResourceTypes,
      createdBy,
      assessmentFilters,
    },
    orderBy: "DISPLAY_SEQUENCE",
    ...(isAssessmentTypeConfigurationEnabled && !_.isEmpty(taskTypeIds)
      ? { taskTypeIds }
      : {}),
  };
};

export const getUnitFlowFeedVariablesMemoized = getMemoizedFunction(
  getUnitFlowFeedVariables
);

const getUnitPlanOutlineFeedVariables = ({
  unitPlanId,
  filters,
  userInfo,
  curriculumType,
}) => {
  const feedVariables = getUnitFlowFeedVariables({
    unitPlanId,
    filters,
    userInfo,
    curriculumType,
  });

  return update(feedVariables, {
    filters: {
      $merge: {
        maxDepth: MAX_FOLDER_DEPTH,
      },
    },
  });
};

export const getUnitPlanOutlineFeedVariablesMemoized = getMemoizedFunction(
  getUnitPlanOutlineFeedVariables
);

/**
 * This function receives data in old format of class flow and returns it in new
 * unit flow revamped format so that common components across class and unit flow
 * can be reused
 */
export const getUpdatedEntityFlowStructure = ({ feedData }) => {
  return _.map(feedData, node => {
    const childrenV2 = _.get(node, "childrenV2", []);

    const item = _.get(node, "item", {});
    const isUnitResource = _.has(item, "attachmentType");
    const hasAttachment = _.has(item, "attachment") || isUnitResource;
    const attachment = isUnitResource ? item : _.get(item, "attachment", {});

    const updatedAttachment = hasAttachment
      ? {
          ...attachment,
          type: attachment?.attachmentType,
          title: attachment?.attachmentTitle,
        }
      : null;

    const updatedItem = hasAttachment ? updatedAttachment : item;

    const resourceType = _.get(node, "resourceType", "");
    const updatedResourceType =
      resourceType == "ENTITY_FLOW_RESOURCE" ? "FILE" : resourceType;

    return {
      ...node,
      childrenV2: { edges: _.map(childrenV2, child => ({ node: child })) },
      attachment: updatedAttachment,
      item: updatedItem,
      resourceType: updatedResourceType,
    };
  });
};

const getDisplaySettings = ({ t }) => {
  return [
    {
      key: "show_outline",
      label: t("classMaterials:show_outline"),
      type: "checkbox",
      hasDivider: true,
    },
    {
      key: "collapse_sections",
      isItemGroup: true,
      options: [
        {
          key: "collapse_all_sections",
          label: t("classMaterials:collapse_all_sections"),
          type: "radio",
        },
        {
          key: "expand_all_sections",
          label: t("classMaterials:expand_all_sections"),
          type: "radio",
        },
      ],
    },
    {
      key: "default_item_placement",
      label: t("classMaterials:default_item_placement"),
      isItemGroup: true,
      options: [
        {
          key: "add_to_top",
          label: t("classMaterials:add_to_top"),
          type: "radio",
        },
        {
          key: "add_to_bottom",
          label: t("classMaterials:add_to_bottom"),
          type: "radio",
        },
      ],
    },
    {
      key: "task_resource_icon",
      label: t("classMaterials:task_resource_icon"),
      isItemGroup: true,
      options: [
        {
          key: "thumbnail_image",
          label: t("classMaterials:thumbnail_image"),
          type: "radio",
        },
        {
          key: "task_resource_type",
          label: t("classMaterials:task_resource_type"),
          type: "radio",
        },
      ],
    },
  ];
};

export const getDisplaySettingsMemoized = getMemoizedLangFunction(
  getDisplaySettings
);

const getDepthWiseSectionOptions = ({ depth, isFolder }) => {
  // this option is only available for sections
  // it creates a new section at same level of current section after its children
  const parallelSectionOption = {
    label: "classMaterials:add_section",
    key: "create_parallel_section",
    icon: <SectionListOutlined />,
  };

  const sectionOption = {
    label: "classMaterials:add_section",
    key: "create_section",
    icon: <SectionListOutlined />,
  };

  const subsectionOption = {
    label: "classMaterials:add_subsection",
    key: "create_subsection",
    icon: <SubsectionListOutlined />,
  };

  /* 'depth' here denotes the depth at which the new item will be createdAt
   * if 'isFolder' is true, that means the trigger element is a section
   * 'isCollapsed' is true then the folder element is 1 level above depth, otherwise same level
   */

  /**
   * Depth 1
   * Can only create sections
   */

  if (depth === 1) {
    return [sectionOption];
  }

  /**
   * Depth 2
   * Files:
   ** Can only create sections under files
   *
   * Folders:
   ** Section + subsection
   */

  if (depth === 2) {
    if (isFolder) {
      return [subsectionOption];
    }

    return [sectionOption];
  }

  /**
   * Depth above 2 till max folder depth
   * Files:
   ** Can only create sections below files
   *
   * Folders:
   ** Both parallel and subsections can be created
   */
  if (_.inRange(depth, 3, MAX_DEPTH)) {
    if (isFolder) {
      return [parallelSectionOption, subsectionOption];
    }

    return [sectionOption];
  }

  // Restrict sections above max folder depth
  if (depth > MAX_FOLDER_DEPTH && !isFolder) {
    return [];
  }

  // Can create a parallel section at max depth
  if (depth === MAX_DEPTH && isFolder) {
    return [parallelSectionOption];
  }

  return [];
};

export const canCreateAssessmentInEditMode = () =>
  ACLStore.can(OPEN_AND_CREATE_ASSIGNMENTS_IN_EDIT_MODE);

// If there is change please also update in combined library (rightPane of unitFlow)
const getAddDropDownOptions = ({
  curriculumType,
  depth,
  isFolder,
  t,
  isTermsExists,
  excludeFolderOptions = false,
  isUnlockedTermExistsForCurrentLearningCourse = true,
}) => {
  const isPYPAssessmentEnabled = ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP);

  const canCreateAssessments = _.includes(
    [
      CURRICULUM_TYPE_DP,
      CURRICULUM_TYPE_MYP,
      ...(isPYPAssessmentEnabled ? [CURRICULUM_TYPE_PYP] : []),
    ],
    curriculumType
  );

  const canCreateWorksheets = ACLStore.can("FeatureFlag:ClassroomWorksheet");
  const canCreateAIChatTask = ACLStore.can(ENABLE_AI_TUTOR);
  const isMapleBearLikeOrg = ACLStore.can("FeatureFlag:IsMapleBearLikeOrg");

  return [
    {
      label: "common:create",
      key: "CREATE",
      isItemGroup: true,
      options: [
        ...(excludeFolderOptions
          ? []
          : getDepthWiseSectionOptions({ depth, isFolder })),
        {
          label: "common:le_label_plural",
          key: "CREATE_LE",
          icon: <LeOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t(
                "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                { label: t("common:le_label_plural") }
              )
            : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
        },
        ...(canCreateAssessments
          ? [
              {
                label: "common:formative_assessments",
                key: "CREATE_FA",
                icon: <FaOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("common:formative_assessments") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
              {
                label: "common:summative_assessments",
                key: "CREATE_SA",
                icon: <SaOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("common:summative_assessments") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
            ]
          : []),
        ...(curriculumType === CURRICULUM_TYPE_UBD
          ? [
              {
                label: "common:assessment",
                key: "CREATE_ASSESSMENT",
                icon: <AssessmentAddOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("common:assessment") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
            ]
          : []),
        ...(canCreateWorksheets
          ? [
              {
                label: "common:worksheet_other",
                key: "CREATE_WORKSHEET",
                value: ASSESSMENT_TYPE_WORKSHEET,
                icon: <WorksheetClipboardOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("common:worksheet_other") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
            ]
          : []),
        ...(canCreateAIChatTask
          ? [
              {
                label: "common:ai_tutor",
                key: CREATE_AI_TUTOR,
                value: ASSESSMENT_TYPE_AI_TUTOR,
                icon: <AiOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("common:ai_tutor") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
            ]
          : []),
        {
          label: "common:note",
          key: "ADD_NOTE",
          icon: <NoteOutlined />,
        },
        {
          label: "common:link",
          key: "ADD_LINK",
          icon: <LinkOutlined />,
        },
        {
          label: "common:upload_from",
          key: "upload_menu",
          isSubMenu: true,
          showDivider: false,
          icon: <UploadOutlined />,
          options: [
            {
              key: "add_google_drive",
              label: "common:google_drive",
              icon: <GoogleDriveColored />,
            },
            {
              key: "add_one_drive",
              label: "common:one_drive",
              icon: <OneDriveColored />,
            },
            {
              key: "upload_device",
              label: "common:device",
              icon: <DeviceDesktopOutlined />,
            },
          ],
        },
        {
          key: "MORE",
          label: "common:more",
          icon: <DotsHorizontalOutlined />,
          isSubMenu: true,
          options: [],
        },
      ],
    },
    {
      label: "common:import_from",
      key: "import_menu",
      isSubMenu: true,
      icon: <ImportOutlined />,
      options: [
        {
          key: "IMPORT_FROM_OTHER_UNIT",
          label: "unitPlan:other_unit_of_this_academic_year",
          icon: <UnitPlanOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:unit_lowercase"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        {
          key: "IMPORT_LE_FROM_LE_LIBRARY",
          label: "common:learning_experience_and_assessment_library",
          icon: <LeLibraryOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:learning_experience_and_assessment_library"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        ...(isMapleBearLikeOrg ||
        !shouldShowImportFromToddleLibrary({ curriculumType })
          ? []
          : [
              {
                key: "IMPORT_FROM_TODDLE_LIBRARY",
                label: "common:toddle_library",
                icon: <JournalOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:import_from_label_no_grading_periods_tooltip",
                      {
                        label: t("common:toddle_library"),
                      }
                    )
                  : t(
                      "course:items_cannot_be_imported_due_to_locked_grading_periods"
                    ),
              },
            ]),
      ],
    },
  ];
};

export const getTemplateIdFromTaskType = (
  taskTypeId,
  gradeId,
  curriculumId,
  isGradeWiseAssessment = false
) => {
  const data = getAssessmentTemplatesFromCache(curriculumId);

  if (_.isEmpty(data)) {
    return null;
  }

  const taskTypes = _.get(data, "taskTypes", []);

  const taskType = _.find(taskTypes, { id: taskTypeId });

  if (_.isEmpty(taskType)) {
    return null;
  }

  const templates = _.get(taskType, "templates", []);

  // Filter templates by grade if grade-wise assessment is enabled and gradeId is provided
  if (isGradeWiseAssessment && gradeId) {
    const filteredTemplates = _.filter(templates, template => {
      const templateGrades = _.get(template, "grades", []);
      return _.some(templateGrades, grade => grade.id === gradeId);
    });

    if (_.isEmpty(filteredTemplates)) {
      return _.get(_.first(templates), "id", null);
    } else {
      return _.get(_.first(filteredTemplates), "id", null);
    }
  }

  // When not filtering by grade, return the first template
  return _.get(_.first(templates), "id", null);
};

const getAddDropDownOptionsV2 = ({
  t,
  curriculumProgramId,
  isTermsExists,
  isUnlockedTermExistsForCurrentLearningCourse = true,
  gradeId,
  depth,
  isFolder,
  excludeFolderOptions = false,
}) => {
  const isMapleBearLikeOrg = ACLStore.can("FeatureFlag:IsMapleBearLikeOrg");

  const assessmentTemplatesData = getAssessmentTemplatesFromCache(
    curriculumProgramId
  );
  const taskTypes = _.get(assessmentTemplatesData, "taskTypes", []);

  const sortedTaskTypes = _.sortBy(taskTypes, "displaySequence");

  const filteredTaskTypes = _.filter(
    sortedTaskTypes,
    taskType =>
      !_.includes([ASSESSMENT_TYPE_QUICK_TASK], _.get(taskType, "type", ""))
  );

  const isGradeWiseAssessment = ACLStore.can(ENABLE_GRADE_WISE_ASSESSMENT);
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const assessmentOptions = _.map(filteredTaskTypes, taskType => {
    const type = _.get(taskType, "type", "");
    const label = _.get(taskType, "label", "");
    const taskTypeId = _.get(taskType, "id", "");
    const color = _.get(taskType, "color", "");

    const templateId = getTemplateIdFromTaskType(
      taskTypeId,
      gradeId,
      curriculumProgramId,
      isGradeWiseAssessment
    );

    const avatarName = getTaskTypeAvatarName({
      type,
      label,
      isAssessmentTypeConfigurationEnabled,
    });

    return {
      taskTypeId,
      label: label,
      key: taskTypeId,
      value: taskTypeId,
      taskType: type,
      templateId,
      icon: (
        <Avatar
          shape="square"
          size="large"
          name={avatarName}
          color={color}
          dsVersion="2.0"
        />
      ),
      disabled: !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
      disabledTooltip: !isTermsExists
        ? t(
            "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
            { label }
          )
        : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
    };
  });

  return [
    {
      label: "common:create",
      key: "CREATE",
      isItemGroup: true,
      options: [
        ...(excludeFolderOptions
          ? []
          : getDepthWiseSectionOptions({ depth, isFolder })),
        ...assessmentOptions,
        {
          label: "common:note",
          key: "ADD_NOTE",
          icon: <NoteOutlined />,
        },
        {
          label: "common:link",
          key: "ADD_LINK",
          icon: <LinkOutlined />,
        },
        {
          label: "common:upload_from",
          key: "upload_menu",
          isSubMenu: true,
          showDivider: false,
          icon: <UploadOutlined />,
          options: [
            {
              key: "add_google_drive",
              label: "common:google_drive",
              icon: <GoogleDriveColored />,
            },
            {
              key: "add_one_drive",
              label: "common:one_drive",
              icon: <OneDriveColored />,
            },
            {
              key: "upload_device",
              label: "common:device",
              icon: <DeviceDesktopOutlined />,
            },
          ],
        },
        {
          key: "MORE",
          label: "common:more",
          icon: <DotsHorizontalOutlined />,
          isSubMenu: true,
          options: [],
        },
      ],
    },
    {
      label: "common:import_from",
      key: "import_menu",
      isSubMenu: true,
      icon: <ImportOutlined />,
      options: [
        {
          key: "IMPORT_FROM_OTHER_UNIT",
          label: "unitPlan:other_unit_of_this_academic_year",
          icon: <UnitPlanOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:unit_lowercase"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        {
          key: "IMPORT_LE_FROM_LE_LIBRARY",
          label: "common:learning_experience_and_assessment_library",
          icon: <LeLibraryOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:learning_experience_and_assessment_library"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        ...(isMapleBearLikeOrg ||
        !shouldShowImportFromToddleLibrary({ curriculumType: null })
          ? []
          : [
              {
                key: "IMPORT_FROM_TODDLE_LIBRARY",
                label: "common:toddle_library",
                icon: <JournalOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:import_from_label_no_grading_periods_tooltip",
                      {
                        label: t("common:toddle_library"),
                      }
                    )
                  : t(
                      "course:items_cannot_be_imported_due_to_locked_grading_periods"
                    ),
              },
            ]),
      ],
    },
  ];
};

export const getAddDropDownOptionsMemoized = getMemoizedLangFunction(
  getAddDropDownOptions
);

export const getAddDropDownOptionsV2Memoized = getMemoizedLangFunction(
  getAddDropDownOptionsV2
);

const getMoveToSectionOption = ({
  t,
  itemType,
  itemDepth,
  maxChildDepth = 0,
  sectionsCount,
}) => {
  // no need to show move to section option if there are
  // no sections in the feed
  if (sectionsCount === 0) return [];

  const isItemFolder = itemType === "FOLDER";
  if (isItemFolder && itemDepth > MAX_FOLDER_DEPTH) return [];

  let itemStructureDepth = 1;

  if (maxChildDepth > 0) {
    itemStructureDepth += maxChildDepth;
    itemStructureDepth -= itemDepth;
  }

  if (isItemFolder) {
    itemStructureDepth += 1;
  }

  const moveToSectionLabel =
    itemDepth === 1
      ? t("common:move_to_section")
      : t("common:move_to_another_section");

  return [
    {
      key: "MOVE_TO_SECTION",
      label: moveToSectionLabel,
      icon: <SwitchArrowsOutlined />,
      disabled: itemStructureDepth > MAX_FOLDER_DEPTH,
      disabledTooltip: t(
        "common:hierarchy_level_restriction_for_section_with_level",
        { level: MAX_DEPTH }
      ),
    },
  ];
};

const getDisabledTooltipForUpdations = ({
  t,
  isGradingPeriodUnlocked,
  actionLabel,
  defaultTooltip = "",
}) => {
  if (!isGradingPeriodUnlocked) {
    return t("course:you_cannot_with_label_since_locked", {
      actionLabel,
    });
  }

  return defaultTooltip;
};

const getCardDropdownOptions = ({
  t,
  type,
  attachmentType,
  itemDepth,
  sectionsCount,
  maxChildDepth,
  academicTerm,
  isExpanded,
  showExpandCollapseAllOption,
  isTermsExists,
  assessmentLabel,
  isSchoolLibrary = false,
  curriculumType,
  assessmentType,
  hasLockedAcademicTermChildren = false,
  isPreviousAcademicYearSelected = false,
  hasEditAccess = true,
}) => {
  const moveToSectionOption = getMoveToSectionOption({
    t,
    itemType: type,
    itemDepth,
    maxChildDepth,
    sectionsCount,
  });
  const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
    academicTerm,
    assessmentType
  );
  const isProSchool = getIsProSchool();

  switch (type) {
    case "FOLDER": {
      return [
        ...(hasEditAccess
          ? [
              {
                key: "SECTION_RENAME",
                label: t("classMaterials:edit_section_title"),
                icon: <PencilLineOutlined />,
              },
            ]
          : []),
        isExpanded
          ? {
              key: "COLLAPSE_ALL_SECTIONS",
              label: showExpandCollapseAllOption
                ? t("classMaterials:collapse_all_sections")
                : t("common:collapse"),
              icon: <CaretOpenedFilled />,
            }
          : {
              key: "EXPAND_ALL_SECTIONS",
              label: showExpandCollapseAllOption
                ? t("classMaterials:expand_all_sections")
                : t("common:expand"),
              icon: <CaretClosedFilled />,
            },
        ...(hasEditAccess ? moveToSectionOption : []),
        ...(hasEditAccess
          ? [
              {
                key: "SECTION_DELETE",
                label: t("common:delete"),
                icon: <DeleteOutlined />,
                disabled: hasLockedAcademicTermChildren,
                disabledTooltip: hasLockedAcademicTermChildren
                  ? t(
                      "course:you_cannot_delete_parent_since_it_has_locked_items",
                      {
                        parentEntityLabel: t("common:folder"),
                      }
                    )
                  : "",
                isDestructive: true,
              },
            ]
          : []),
      ];
    }

    case "FILE": {
      const isLink = attachmentType === "LINK";
      const isNote = attachmentType === "NOTE";

      return [
        ...(hasEditAccess && isNote
          ? [
              {
                key: "NOTE_EDIT",
                label: t("common:edit"),
                icon: <PencilLineOutlined />,
              },
            ]
          : []),
        ...(hasEditAccess && isLink
          ? [
              {
                key: "EDIT_LINK",
                label: t("common:edit"),
                icon: <PencilLineOutlined />,
              },
            ]
          : []),
        ...(hasEditAccess && !isLink && !isNote
          ? [
              {
                key: "FILE_RENAME",
                label: t("common:rename"),
                icon: <PencilLineOutlined />,
              },
            ]
          : []),
        {
          key: "FILE_VIEW",
          label: t("common:view"),
          icon: <EyeOutlined />,
        },
        ...(hasEditAccess
          ? [
              {
                key: "FILE_DUPLICATE",
                label: t("common:create_a_duplicate"),
                icon: <AddToLibraryOutlined />,
              },
            ]
          : []),
        ...(hasEditAccess ? moveToSectionOption : []),
        ...(!isLink && !isNote
          ? [
              {
                key: "FILE_DOWNLOAD",
                label: t("common:download"),
                icon: <DownloadOutlined />,
              },
            ]
          : []),
        ...(hasEditAccess
          ? [
              {
                key: "FILE_DELETE",
                label: t("common:delete"),
                icon: <DeleteOutlined />,
                isDestructive: true,
              },
            ]
          : []),
      ];
    }

    case "ASSESSMENT": {
      return [
        ...(hasEditAccess
          ? [
              {
                key: "ASSESSMENT_EDIT",
                label: t("common:edit"),
                icon: <PencilLineOutlined />,
                disabled: !isGradingPeriodUnlocked,
                disabledTooltip: getDisabledTooltipForUpdations({
                  t,
                  isGradingPeriodUnlocked,
                  actionLabel: t("common:edit"),
                }),
              },
            ]
          : []),
        {
          key: "ASSESSMENT_VIEW",
          label: t("common:view"),
          icon: <EyeOutlined />,
        },
        ...(hasEditAccess
          ? [
              {
                label: t("common:copy"),
                key: "duplicate_menu",
                isSubMenu: true,
                icon: <CopyOutlined />,
                options: [
                  {
                    key: "ASSESSMENT_REPEAT",
                    label: t("unitPlan:keep_connected"),
                    subText: t("unitPlan:keep_connected_subtext"),
                    icon: <LinkSimpleHorizontalOutlined />,
                  },
                  {
                    key: "FILE_DUPLICATE",
                    label: t("common:create_a_duplicate"),
                    subText: t("unitPlan:duplicate_item_subtext"),
                    disabled: !isGradingPeriodUnlocked,
                    disabledTooltip: getDisabledTooltipForUpdations({
                      t,
                      isGradingPeriodUnlocked,
                      actionLabel: t("common:duplicate"),
                    }),
                    icon: <AddToLibraryOutlined />,
                  },
                ],
              },
            ]
          : []),
        // Only show "assign to students" option for non pro schools and non-previous AYs
        ...(!isProSchool && !isPreviousAcademicYearSelected
          ? [
              {
                key: "ASSESSMENT_ASSIGN",
                label: t("common:assign_to_students"),
                disabled:
                  !isGradingPeriodUnlocked || !isTermsExists || isSchoolLibrary,
                disabledTooltip: isSchoolLibrary
                  ? null
                  : !isGradingPeriodUnlocked
                  ? t("course:you_cannot_with_label_since_locked", {
                      actionLabel: t("common:assign_to_students"),
                    })
                  : t(
                      "classRoom:assign_to_students_no_grading_periods_tooltip_with_label_plural",
                      {
                        label: assessmentLabel,
                      }
                    ),
                icon: <StudentAddOutlined />,
              },
            ]
          : []),
        ...(hasEditAccess ? moveToSectionOption : []),
        {
          icon: <PrintOutlined />,
          key: "ASSESSMENT_PRINT",
          label: t("common:print"),
        },
        ...(hasEditAccess
          ? [
              {
                key: "CHANGE_TASK",
                label: t("common:change_task_type"),
                disabled:
                  !isGradingPeriodUnlocked ||
                  _.includes(
                    [ASSESSMENT_TYPE_WORKSHEET, ASSESSMENT_TYPE_AI_TUTOR],
                    assessmentType
                  ),
                disabledTooltip: getDisabledTooltipForUpdations({
                  t,
                  isGradingPeriodUnlocked,
                  actionLabel: t("common:change_task_type"),
                  defaultTooltip: t(
                    "classRoom:change_task_type_dropdown_disabled_tooltip"
                  ),
                }),
                icon: <ReloadArrowOutlined />,
                shouldHide:
                  curriculumType === CURRICULUM_TYPE_PYP &&
                  !ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP),
              },
              {
                key: "ASSESSMENT_DELETE",
                label: t("common:delete"),
                icon: <DeleteOutlined />,
                disabled: !isGradingPeriodUnlocked,
                disabledTooltip: getDisabledTooltipForUpdations({
                  t,
                  isGradingPeriodUnlocked,
                  actionLabel: t("common:delete"),
                }),
                isDestructive: true,
              },
            ]
          : []),
      ];
    }
  }

  return [];
};

export const getCardDropdownOptionsMemoized = getMemoizedLangFunction(
  getCardDropdownOptions
);

const getAssignmentModalData = ({ t }) => {
  return {
    mode: "create",
    creationMode: "ASSIGN_TO_CLASS",
    steps: [
      {
        label: t("common:edit_with_label", {
          label: t("common:post"),
        }),
        value: "ASSESSMENT_DETAILS",
      },
      {
        label: t("classRoom:post_settings"),
        value: "SETTINGS",
      },
    ],
    contentType: "ASSESSMENT",
    createHeader: t("common:assign_to_students"),
    updateUnitPlanDataInRedux: false,
  };
};

export const getAssignmentModalDataMemoized = getMemoizedLangFunction(
  getAssignmentModalData
);

export const getToastMsgForResourceUpdate = resourceType => {
  let msg = "";
  switch (resourceType) {
    case "LINK": {
      msg = "classMaterials:toast_msg_for_link_update";
      break;
    }
    case "NOTE": {
      msg = "classMaterials:toast_msg_for_note_update";
      break;
    }
    case "FILE": {
      msg = "classMaterials:toast_msg_for_resource_rename";
      break;
    }
    default: {
      msg = "classMaterials:toast_msg_for_section_rename";
      break;
    }
  }

  return {
    msg,
    type: "success",
    position: "toast-bottom-left",
  };
};

export const checkIsUnitFlowPage = url => {
  return _.includes(url, "/unitFlow");
};

export const getResourceData = ({ item, variables }) => {
  const {
    id,
    depth,
    parent,
    label,
    resourceType,
    childrenHierarchyInfo,
  } = item;
  const childFeedData = getUnitFlowItemFromCache({ id, variables });

  if (!_.isEmpty(childFeedData)) {
    return { ...childFeedData, depth, childrenHierarchyInfo };
  }

  return {
    id,
    label,
    displaySequence: 1,
    depth,
    resourceType,
    isSkeleton: true,
    item: null,
    parent,
    childrenHierarchyInfo: null,
    childrenV2: [],
    isRepeated: false,
    repeatedResources: [],
    __typename: "EntityFlowResource",
  };
};

const getUnitResourcesVariables = ({
  parentId,
  searchText = "",
  first = 25,
  after = null,
}) => {
  return {
    parentId,
    filters: {
      type: "UNIT_RESOURCE",
      searchText,
      itemTypes: ["FILE"],
    },
    first,
    after,
  };
};

export const getUnitResourcesVariablesMemoized = getMemoizedFunction(
  getUnitResourcesVariables
);

export const convertToEntityFlowResource = ({
  resource,
  parent = null,
  unitFlowFeed,
}) => {
  if (!resource) return null;

  // default data for optimistic response
  const response = {
    childrenHierarchyInfo: null,
    childrenV2: [],
    parent,
    id: generateRandomId(),
    repeatedResources: [],
    displaySequence: 0,
    __typename: "EntityFlowResource",
  };

  const isAttachmentResource = _.has(resource, "attachment");
  const item = {};
  if (isAttachmentResource) {
    item.id = _.get(resource, "id", "");
    item.__typename = "EntityFlowResource";
    item.attachment = {};

    const attachment = _.get(resource, "attachment", {});
    item.attachment.id = _.get(attachment, "id", "");
    item.attachment.__typename = "Attachment";
    item.attachment.attachmentTitle = _.get(attachment, "title", "");
    item.attachment.attachmentType = _.get(attachment, "type", "");
    item.attachment.mimeType = _.get(attachment, "mimeType", null);
    item.attachment.name = _.get(attachment, "name", null);
    item.attachment.thumbUrl = _.get(attachment, "thumbUrl", null);
    item.attachment.signedUrl = _.get(attachment, "signedUrl", null);
    item.attachment.url = _.get(attachment, "url", null);

    response.resourceType = "ENTITY_FLOW_RESOURCE";

    const isNote = attachment?.type === "NOTE";
    const attachmentLabel = isNote
      ? _.get(attachment, "title", "--")
      : _.get(attachment, "name", "");
    response.label = _.get(resource, "label") ?? attachmentLabel;

    response.isRepeated = false;
  } else {
    item.__typename = "Assessment";
    item.type = ""; // cannot be fetched from org resource, change if causing error
    item.id = _.get(resource, "id", "");
    item.title = _.get(resource, "title", {});
    item.image = _.get(resource, "image", {});
    item.category = _.get(resource, "category", null);
    item.assessmentType = _.get(resource, "assessmentType", {});
    item.isImported = _.get(resource, "isImported", false);
    item.academicTerm = _.get(resource, "academicTerm", {});

    response.resourceType = "ASSESSMENT";
    response.label = _.get(resource, "title.value", "");
    response.isRepeated = _.some(
      unitFlowFeed?.edges,
      ({ node }) => node?.item?.id === item.id
    );
  }
  response.item = item;

  return response;
};

export const getModifiedFiltersForUnitFlowImport = filters => {
  const {
    itemTypes: attachmentTypes,
    resourceTypes,
    searchText,
    assessmentFilters,
  } = filters;

  const itemTypes = _.map(resourceTypes, type => {
    if (type === "ORGANIZATION_RESOURCE") return "FILE";
    return type;
  });

  return {
    entityFlowResourceFilter: {
      itemTypes,
      searchText,
      assessmentFilters,
      attachmentTypes,
    },
  };
};

export const getUnitFlowFeedEdges = feedData =>
  _.get(feedData, "node.unitFlowFeed.unitFlowFlatFeed.edges", []);

export const getUnitFlowFeedTotalCount = feedData =>
  _.get(
    feedData,
    "node.unitFlowFeed.unitFlowFlatFeed.resourceBasedCount.totalCount",
    0
  );

export const getUnitFlowOutlineFeed = data =>
  _.get(data, "unitFlowFeed.unitFlowOutline", []);

export const getUnitResourcesFeed = data =>
  _.get(data, "node.unitFlowFeed.unitFlowFlatFeed", []);

export const getImportFromUnitFlowInput = ({
  selectedInput,
  putAfterItemId,
  parentId,
  sourceId,
  sourceModuleType,
  unitFlowId,
  filters,
}) => {
  return {
    ..._.pick(selectedInput, ["includedIds", "excludedIds", "selectAll"]),
    putAfterItemId, // For inline import - id of the item after which resources should be added or null
    parentId, // For inline import - folder id or null
    sourceId,
    sourceModuleType,
    destinationId: unitFlowId,
    filters,
  };
};

export const getCreatedUnitFlowResourcesData = data =>
  _.get(data, "data.planner.createEntityFlowResource");

// generate FilterOptions
export const generateFilterOptions = ({
  t,
  filters,
  addedByOptions,
  itemTypeOptions,
}) => {
  const options = {};
  const defaultSelectedOptions = {};

  const userInfo = getUserInfo();
  const userType = userInfo.user_type;
  const isUserTeacher = isUserTypeStaff({ userType });

  const populateOptions = ({
    filterName,
    filterOptions,
    title,
    shouldPopulate = true,
    isSingleSelect = false,
  }) => {
    if (!shouldPopulate) return;

    options[filterName] = {
      options: filterOptions,
      isSingleSelect,
      title: title,
    };

    const filtersValue = _.get(filters, filterName, []);

    if (filtersValue === "ALL") {
      defaultSelectedOptions[filterName] = _.flatMap(filterOptions, option => {
        const nestedOptions = option.options;
        if (nestedOptions) return _.map(nestedOptions, "value");
        return option.value;
      });
    } else {
      defaultSelectedOptions[filterName] = filtersValue;
    }
  };

  // Item type
  populateOptions({
    filterName: "selectedItemTypes",
    filterOptions: itemTypeOptions,
    title: t("classMaterials:item_types_label"),
  });

  // Added by
  populateOptions({
    filterName: "selectedAddedBy",
    filterOptions: addedByOptions,
    title: t("common:added_by"),
    isSingleSelect: true,
    shouldPopulate: isUserTeacher,
  });

  return { options, defaultSelectedOptions };
};

const getCardActionsMemoized = [];
